import {Box, ButtonBase, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import {SxProps, Theme} from "@mui/material/styles";
import {Datepicker} from "@mobiscroll/react";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import "@mobiscroll/react/dist/css/mobiscroll.scss";
import {addDays, formatFullDMY, isToday, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {
    DateRange,
    DatePickerChangeReason,
    DatePickerSize,
    DATE_PICKER_SIZE_TOKENS,
    StepButtonsPlacement,
    insideStepButtonSx,
    mergeSx,
    stepButtonSx,
    toPickerDate,
    triggerStateSx,
    useCalendarLocale,
    useIsMobile,
    useMobiscrollThemeStyles,
    useStepButtonsInside,
    weekdayLabel,
    WEEKDAY_SLOT,
    DATE_SLOT,
    DATE_SLOT_COMPACT,
    formatCompactDMY,
    NEW_DATE_PICKER_POPUP_CLASS,
} from "./datePickerShared";

/**
 * Minimal date-range picker: our own trigger (start–end label + optional step / today buttons)
 * over a headless Mobiscroll range calendar. The left step extends the start backwards and the
 * right step extends the end forwards; "today" anchors the start while preserving the range
 * length. Single-date selection lives in {@link SingleDatePicker} — these two are intentionally
 * separate so each can be tweaked freely.
 */
export interface RangeDatePickerProps {
    value?: DateRange;
    onChange?: (range: DateRange, reason: DatePickerChangeReason) => void;
    disabled?: boolean;
    showWeekday?: boolean;
    showStepButtons?: boolean;
    showTodayButton?: boolean;
    stepButtonsPlacement?: StepButtonsPlacement;
    /** "medium" (40px, default) or "small" (36px). Matches MUI input sizing. */
    size?: DatePickerSize;
    /** Optional fixed trigger width (px). Sizes to content otherwise. */
    width?: number;
    sx?: SxProps<Theme>;
}

type PickerChangeEvent = {value?: unknown};

export function RangeDatePicker(props: RangeDatePickerProps) {
    const {t} = useTranslation();
    const {language, calendarLocale} = useCalendarLocale();
    const accent = useMobiscrollThemeStyles();

    const pickerRef = useRef<Datepicker>(null);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

    const showWeekday = props.showWeekday ?? true;
    const showStepButtons = props.showStepButtons ?? true;
    const showTodayButton = props.showTodayButton ?? true;
    const disabled = props.disabled ?? false;

    const size = props.size ?? "medium";
    const sz = DATE_PICKER_SIZE_TOKENS[size];

    const placement = props.stepButtonsPlacement ?? "auto";
    const isMobile = useIsMobile();
    const stepInsideAuto = useStepButtonsInside(placement, showStepButtons);
    // Mobile-compact range view (default "auto" placement only): two full "DD.MM.YYYY" dates plus
    // the weekday overflow a 375px screen, and that content — not the arrow placement — is what
    // drives the width. So on mobile we drop the weekday, switch to a 2-digit year, and keep the
    // step arrows OUTSIDE the field (a clean flank rather than four controls crammed in one box).
    // Explicit "inside"/"outside" placements and all desktop widths are left untouched.
    const compact = isMobile && placement === "auto";
    const stepInside = compact ? false : stepInsideAuto;

    const effectiveShowWeekday = showWeekday && !compact;
    const dateSlot = compact ? DATE_SLOT_COMPACT : DATE_SLOT;
    const formatDate = compact ? formatCompactDMY : formatFullDMY;

    // Fall back to today so the picker is always controlled even without a value.
    const today = startOfDay(new Date());
    const range: DateRange = props.value
        ? {start: startOfDay(props.value.start), end: startOfDay(props.value.end)}
        : {start: today, end: today};
    const todaySelected = isToday(range.start) && isToday(range.end);

    const hasFixedWidth = props.width != null;
    const triggerWidth: number | string = hasFixedWidth ? (props.width as number) : "max-content";

    const renderDateLabel = (date: Date) => {
        // Fixed-width, left-aligned slot so proportional digits (League Spartan's "1" is
        // ~half-width) can't reflow the max-content trigger as the date changes. See DATE_SLOT.
        const dateBox = (
            <Box
                component="span"
                sx={{
                    display: "inline-block",
                    width: dateSlot,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    textAlign: "left",
                }}
            >
                {formatDate(date)}
            </Box>
        );

        if (!effectiveShowWeekday) {
            return dateBox;
        }

        return (
            <Box component="span" sx={{display: "inline-flex", alignItems: "center", minWidth: 0}}>
                <Box
                    component="span"
                    sx={{
                        flex: `0 0 ${WEEKDAY_SLOT}`,
                        width: WEEKDAY_SLOT,
                        mr: 0.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                    }}
                >
                    {weekdayLabel(language, date)}
                </Box>

                {dateBox}
            </Box>
        );
    };

    const renderTriggerLabel = () => (
        <Box component="span" sx={{display: "inline-flex", alignItems: "center", minWidth: 0}}>
            {renderDateLabel(range.start)}

            <Box component="span" sx={{mx: 0.5, flex: "0 0 auto"}}>
                -
            </Box>

            {renderDateLabel(range.end)}
        </Box>
    );

    const openPicker = () => {
        if (!disabled) {
            pickerRef.current?.open();
        }
    };

    const extendRange = (direction: -1 | 1) => {
        if (disabled) {
            return;
        }
        props.onChange?.({
            start: direction === -1
                ? startOfDay(addDays(range.start, -1))
                : range.start,
            end: direction === 1
                ? startOfDay(addDays(range.end, 1))
                : range.end,
        }, "step");
    };

    const goToToday = () => {
        if (disabled) {
            return;
        }
        props.onChange?.({start: today, end: today}, "today");
    };

    const handlePickerChange = ({value: raw}: PickerChangeEvent) => {
        const rawValues = Array.isArray(raw) ? raw : [raw, raw];
        const start = toPickerDate(rawValues[0]);
        // Mid-selection Mobiscroll emits the start alone. Echoing it back as start–start is what keeps
        // props.value in sync with its internal value — refuse it and Mobiscroll re-reads props and
        // throws the half-picked selection away.
        const end = toPickerDate(rawValues[1] ?? rawValues[0]) ?? start;

        if (!start || !end) {
            return;
        }
        props.onChange?.({start, end}, "selection");
    };

    const stepButton = (direction: -1 | 1) => (
        <IconButton
            aria-label={direction === -1 ? t("Previous day") : t("Next day")}
            disabled={disabled}
            onClick={() => extendRange(direction)}
            sx={stepInside ? insideStepButtonSx(size) : stepButtonSx(size)}
        >
            {direction === -1 ? <ChevronLeftIcon sx={{fontSize: sz.stepIcon}}/> : <ChevronRightIcon sx={{fontSize: sz.stepIcon}}/>}
        </IconButton>
    );

    return (
        <Box sx={[{display: "inline-flex", alignItems: "center"}, ...mergeSx(props.sx)]}>
            <Stack direction="row" alignItems="center" spacing={stepInside ? 0 : 1}>
                {showStepButtons && !stepInside && stepButton(-1)}

                <Box
                    ref={setAnchorElement}
                    aria-disabled={disabled ? "true" : undefined}
                    sx={{
                        width: triggerWidth,
                        maxWidth: "100%",
                        height: sz.height,
                        display: "flex",
                        alignItems: "center",
                        boxSizing: "border-box",
                        borderRadius: {xs: 1, md: 2},
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.23)",
                        backgroundColor: "background.paper",
                        boxShadow: "none",
                        overflow: "hidden",
                        ...triggerStateSx(disabled, accent),
                    }}
                >
                    {showStepButtons && stepInside && stepButton(-1)}

                    {showTodayButton && (
                        <Tooltip title={t("Set to today")} arrow>
                            <span>
                                <IconButton
                                    aria-label={t("Set to today")}
                                    disabled={disabled}
                                    size="small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        goToToday();
                                    }}
                                    sx={{
                                        width: sz.todayButton,
                                        height: sz.todayButton,
                                        mx: 0.25,
                                        borderRadius: 1,
                                        color: todaySelected ? "secondary.main" : "text.secondary",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                            color: todaySelected ? "secondary.main" : "text.primary",
                                        },
                                    }}
                                >
                                    {todaySelected
                                        ? <CalendarMonthIcon sx={{fontSize: sz.todayIcon}}/>
                                        : <EventRepeatOutlinedIcon sx={{fontSize: sz.todayIcon}}/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    <ButtonBase
                        disabled={disabled}
                        onClick={openPicker}
                        aria-label={t("Select date range")}
                        sx={{
                            flex: hasFixedWidth ? 1 : "0 0 auto",
                            alignSelf: "stretch",
                            minWidth: 0,
                            justifyContent: "flex-start",
                            pl: showTodayButton ? 1 : stepInside ? 0.75 : 1.5,
                            pr: stepInside ? 0.75 : 1.5,
                            color: disabled ? "text.disabled" : "text.primary",
                            textAlign: "left",
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        <Typography
                            noWrap
                            sx={{
                                ...(hasFixedWidth ? {width: "100%"} : {}),
                                minWidth: 0,
                                fontSize: sz.fontSize,
                                fontWeight: 500,
                                lineHeight: sz.lineHeight,
                                mt: sz.textNudge,
                                textAlign: "left",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {renderTriggerLabel()}
                        </Typography>
                    </ButtonBase>

                    {showStepButtons && stepInside && stepButton(1)}
                </Box>

                {showStepButtons && !stepInside && stepButton(1)}
            </Stack>

            <Datepicker
                ref={pickerRef}
                disabled={disabled}
                value={[range.start, range.end]}
                onChange={handlePickerChange}
                locale={calendarLocale}
                cssClass={NEW_DATE_PICKER_POPUP_CLASS}
                theme="material"
                themeVariant="light"
                dateFormat={showWeekday ? "DDD DD.MM.YYYY" : "DD.MM.YYYY"}
                controls={["calendar"]}
                firstDay={1}
                select="range"
                showInput={false}
                showOnClick={false}
                showOnFocus={false}
                anchor={anchorElement || undefined}
                responsive={{
                    xsmall: {controls: ["calendar"], display: "bottom", touchUi: true},
                    small: {controls: ["calendar"], display: "anchored", touchUi: false},
                    custom: {breakpoint: 800, controls: ["calendar"], display: "anchored", touchUi: false},
                }}
            />
        </Box>
    );
}

export default RangeDatePicker;
